'use client';
import React from 'react';
import { BehaviorCorrelation } from '@/lib/services/forgeBehaviorService';

interface CorrelationCardProps {
  correlation: BehaviorCorrelation;
}

function DiffBadge({ value, unit = '', invert = false }: { value: number; unit?: string; invert?: boolean }) {
  const isPositive = invert ? value < 0 : value > 0;
  const isNeutral = Math.abs(value) < 0.5;
  const formatted = value > 0 ? `+${value.toFixed(1)}` : value.toFixed(1);

  if (isNeutral) {
    return (
      <span className="text-xs font-bold text-muted-foreground">
        ~0{unit}
      </span>
    );
  }

  return (
    <span className={`text-xs font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
      {formatted}{unit}
    </span>
  );
}

export default function CorrelationCard({ correlation }: CorrelationCardProps) {
  const calPct = correlation.withoutCalories > 0
    ? ((correlation.caloriesDiff / correlation.withoutCalories) * 100)
    : 0;

  const metrics = [
    { label: 'Calories', with: Math.round(correlation.withCalories), without: Math.round(correlation.withoutCalories), diff: Math.round(correlation.caloriesDiff), unit: 'kcal', invertGood: true },
    { label: 'Protein', with: Math.round(correlation.withProtein), without: Math.round(correlation.withoutProtein), diff: Math.round(correlation.proteinDiff), unit: 'g', invertGood: false },
    { label: 'Sleep', with: correlation.withSleep.toFixed(1), without: correlation.withoutSleep.toFixed(1), diff: correlation.sleepDiff, unit: 'h', invertGood: false },
    { label: 'Steps', with: Math.round(correlation.withSteps).toLocaleString(), without: Math.round(correlation.withoutSteps).toLocaleString(), diff: Math.round(correlation.stepsDiff), unit: '', invertGood: false },
    { label: 'Forge Score', with: Math.round(correlation.withForgeScore), without: Math.round(correlation.withoutForgeScore), diff: Math.round(correlation.forgeScoreDiff), unit: '', invertGood: false },
  ];

  return (
    <div className="card-forge p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">
            {correlation.behaviorLabel} Impact
          </h3>
          <p className="text-2xs text-muted-foreground mt-0.5">
            {correlation.daysWithBehavior} days with · {correlation.daysWithout} days without
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold text-muted-foreground">CALORIE SHIFT</div>
          <div className={`text-lg font-black ${Math.abs(calPct) < 2 ? 'text-muted-foreground' : calPct > 0 ? 'text-amber-400' : 'text-green-400'}`}>
            {calPct > 0 ? '+' : ''}{calPct.toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {metrics.map(m => (
          <div key={m.label} className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-20 shrink-0">{m.label}</span>
            <div className="flex-1 flex items-center gap-2">
              <span className="text-xs text-foreground font-medium w-16 text-right">{m.with}{m.unit}</span>
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground w-16">{m.without}{m.unit}</span>
            </div>
            <div className="w-14 text-right">
              <DiffBadge value={typeof m.diff === 'number' ? m.diff : 0} unit={m.unit} invert={m.invertGood} />
            </div>
          </div>
        ))}
      </div>

      {/* Correlation insights */}
      <div className="space-y-1.5 pt-2 border-t border-border">
        {Math.abs(calPct) > 5 && (
          <p className="text-xs text-muted-foreground">
            <span className="text-primary font-medium">Possible Correlation:</span>{' '}
            When {correlation.behaviorLabel.toLowerCase()} is logged, average calorie intake{' '}
            {calPct > 0 ? 'increases' : 'decreases'} by {Math.abs(calPct).toFixed(0)}%.
          </p>
        )}
        {Math.abs(correlation.sleepDiff) > 0.2 && (
          <p className="text-xs text-muted-foreground">
            <span className="text-primary font-medium">Possible Correlation:</span>{' '}
            When {correlation.behaviorLabel.toLowerCase()} is logged, average sleep duration{' '}
            {correlation.sleepDiff > 0 ? 'increases' : 'decreases'} by {Math.abs(correlation.sleepDiff * 60).toFixed(0)} minutes.
          </p>
        )}
        {Math.abs(correlation.forgeScoreDiff) > 2 && (
          <p className="text-xs text-muted-foreground">
            <span className="text-primary font-medium">Possible Correlation:</span>{' '}
            When {correlation.behaviorLabel.toLowerCase()} is logged, average Forge Score{' '}
            {correlation.forgeScoreDiff > 0 ? 'increases' : 'decreases'} by {Math.abs(correlation.forgeScoreDiff).toFixed(1)} points.
          </p>
        )}
        {Math.abs(calPct) <= 5 && Math.abs(correlation.sleepDiff) <= 0.2 && Math.abs(correlation.forgeScoreDiff) <= 2 && (
          <p className="text-xs text-muted-foreground">
            No significant correlation detected across tracked metrics.
          </p>
        )}
      </div>

      <p className="text-2xs text-muted-foreground/50 italic">
        Correlation does not necessarily equal causation. These are observations only.
      </p>
    </div>
  );
}
